<template>
  <div class="bg-white h-screen pt-8">
    <div class="container mx-auto px-8 pl-8">
      <div class="flex justify-between justify-items-center mb-3">
        <!-- Title -->
        <p class="font-inter font-semibold text-xl">Vehicle Trips</p>
        <!-- New Trip Form Component -->
        <NewTripFormComponent @upload-success="fetchVehicleTrips" />
      </div>

      <hr />
      <!-- Filters tab starts here -->
      <div class="flex space-x-3 space-between justify-start">
        <!-- Search SO Number starts here -->
        <div class="relative my-3">
          <div
            class="absolute inset-y-0 rtl:inset-r-0 start-0 flex items-center ps-3 pointer-events-none"
          >
            <font-awesome-icon
              :icon="['fas', 'magnifying-glass']"
              class="text-gray-400"
            />
          </div>
          <input
            v-model="filters.SONumber"
            type="text"
            id="so-search"
            class="block ps-10 pe-10 py-2 text-sm text-gray-900 border border-gray-300 rounded-full w-fit font-inter"
            placeholder="Sales Order No"
          />
          <div
            v-if="filters.SONumber"
            @click="filters.SONumber = ''"
            class="absolute cursor-pointer inset-y-0 rtl:inset-r-0 end-0 flex items-center pe-3"
          >
            <font-awesome-icon
              :icon="['fas', 'x']"
              class="text-gray-400 w-3 h-3"
            />
          </div>
        </div>
        <!-- Search SO Number ends here -->
        <!-- Search vehicle field starts here -->
        <div class="relative my-3">
          <div
            class="absolute inset-y-0 rtl:inset-r-0 start-0 flex items-center ps-3 pointer-events-none"
          >
            <font-awesome-icon
              :icon="['fas', 'magnifying-glass']"
              class="text-gray-400"
            />
          </div>
          <input
            v-model="filters.Vehicle"
            type="text"
            id="vehicle-search"
            class="block ps-10 py-2 text-sm text-gray-900 border border-gray-300 rounded-full w-fit font-inter"
            placeholder="Search vehicle"
          />
          <div
            v-if="filters.Vehicle"
            @click="filters.Vehicle = ''"
            class="absolute cursor-pointer inset-y-0 rtl:inset-r-0 end-0 flex items-center pe-3"
          >
            <font-awesome-icon
              :icon="['fas', 'x']"
              class="text-gray-400 w-3 h-3"
            />
          </div>
        </div>
        <!-- Search vehicle field ends here -->
        <!-- Origin search field starts here -->
        <div class="relative my-3">
          <div
            class="absolute inset-y-0 rtl:inset-r-0 start-0 flex items-center ps-3 pointer-events-none"
          >
            <font-awesome-icon
              :icon="['fas', 'location-dot']"
              class="text-gray-400"
            />
          </div>
          <input
            v-model="filters.Origin"
            type="text"
            id="origin-search"
            class="block ps-10 py-2 text-sm text-gray-900 border border-gray-300 rounded-full w-fit font-inter"
            placeholder="Origin"
          />
          <div
            v-if="filters.Origin"
            @click="filters.Origin = ''"
            class="absolute cursor-pointer inset-y-0 rtl:inset-r-0 end-0 flex items-center pe-3"
          >
            <font-awesome-icon
              :icon="['fas', 'x']"
              class="text-gray-400 w-3 h-3"
            />
          </div>
        </div>
        <!-- Origin search field ends here -->
        <!-- Destination search field starts here -->
        <div class="relative my-3">
          <div
            class="absolute inset-y-0 rtl:inset-r-0 start-0 flex items-center ps-3 pointer-events-none"
          >
            <font-awesome-icon
              :icon="['fas', 'location-dot']"
              class="text-gray-400"
            />
          </div>
          <input
            v-model="filters.Destination"
            type="text"
            id="destination-search"
            class="block ps-10 py-2 text-sm text-gray-900 border border-gray-300 rounded-full w-fit font-inter"
            placeholder="Destination"
          />
          <div
            v-if="filters.Destination"
            @click="filters.Destination = ''"
            class="absolute cursor-pointer inset-y-0 rtl:inset-r-0 end-0 flex items-center pe-3"
          >
            <font-awesome-icon
              :icon="['fas', 'x']"
              class="text-gray-400 w-3 h-3"
            />
          </div>
        </div>
        <!-- Destination search field ends here -->
      </div>
      <!-- Filters tab ends here -->
      <hr />
      <div class="relative border-r border-l">
        <div class="overflow-x-auto">
          <table class="w-full text-sm text-left text-black">
            <thead
              class="text-xs text-left text-black uppercase font-inter border-b font-light"
            >
              <tr>
                <th scope="col" class="px-6 py-3 border-r"></th>
                <th scope="col" class="px-6 py-3">SO Number</th>
                <th scope="col" class="px-6 py-3">Goods</th>
                <th scope="col" class="px-6 py-3">Status</th>
                <th scope="col" class="px-6 py-3">Vehicle</th>
                <!-- <th scope="col" class="px-6 py-3">Driver</th> -->
                <th scope="col" class="px-6 py-3">From</th>
                <th scope="col" class="px-6 py-3">Destination</th>
                <th scope="col" class="px-6 py-3">Date</th>
              </tr>
            </thead>
            <tbody v-if="vehicleTripsList.length > 0">
              <tr
                v-for="(trip, index) in vehicleTripsList"
                :key="index"
                class="border-b hover:bg-gray-100 text-gray-900 font-inter"
              >
                <th
                  scope="row"
                  class="px-6 py-4 font-medium whitespace-nowrap border-r"
                >
                  {{ index + 1 }}
                </th>
                <td class="px-6 py-4 text-sky-800 font-bold">
                  {{ trip.SalesOrderNo }}
                </td>
                <td class="px-6 py-4">{{ trip.CargoType }}</td>
                <td class="px-6 py-4">
                  <div class="flex items-center">
                    <div class="h-2 w-2 rounded-full bg-red-700"></div>
                    <p class="ml-2 text-red-700">{{ trip.Status }}</p>
                  </div>
                </td>
                <td class="px-6 py-4">{{ trip.VehicleID.NumberPlate }}</td>
                <!-- <td class="px-6 py-4">{{ trip.DriverID }}</td> -->
                <td class="px-6 py-4">{{ trip.PickUp }}</td>
                <td class="px-6 py-4">{{ trip.Destination }}</td>
                <td class="px-6 py-4">
                  {{
                    $store.getters.getTimestampzFormattedDate(trip.CreatedAt)
                  }}
                </td>
              </tr>
            </tbody>
            <div v-else>
              <p class="font-inter ml-5 my-5 text-gray-700">
                No vehicle trips found
              </p>
            </div>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import NewTripFormComponent from "../components/NewTripFormComponent.vue";
import DataRepository from "@/infrastructure/services/DataRepository";
export default {
  components: {
    NewTripFormComponent,
  },
  computed: {
    vehicleTripsList() {
      return this.vehicleTripsArray.filter((trip) => {
        // Filter by SO Number
        if (
          this.filters.SONumber &&
          trip.SalesOrderNo.toLowerCase().indexOf(
            this.filters.SONumber.toLowerCase()
          ) == -1
        ) {
          return false;
        }
        // Filter by Vehicle
        if (
          this.filters.Vehicle &&
          trip.VehicleID.NumberPlate.toLowerCase().indexOf(
            this.filters.Vehicle.toLowerCase()
          ) == -1
        ) {
          return false;
        }
        // Filter by Origin
        if (
          this.filters.Origin &&
          trip.PickUp.toLowerCase().indexOf(
            this.filters.Origin.toLowerCase()
          ) == -1
        ) {
          return false;
        }
        // Filter by Destination
        if (
          this.filters.Destination &&
          trip.Destination.toLowerCase().indexOf(
            this.filters.Destination.toLowerCase()
          ) == -1
        ) {
          return false;
        }

        return true;
      });
    },
  },
  data() {
    return {
      vehicleTripsArray: [],
      filters: {},
    };
  },
  methods: {
    async fetchVehicleTrips() {
      const result = await DataRepository.fetchVehicleTrips();
      if (result.success && !result.error) {
        this.vehicleTripsArray = result.success;
      }
    },
  },
  mounted() {
    this.fetchVehicleTrips();
  },
};
</script>
